import { usePlayer } from '../../stores/player';
import { useRoomTuning } from '../../stores/room';
import type { Room } from './api';

/**
 * Starting and stopping listening to a broadcast.
 *
 * ## Why these are functions and not two lines at each call site
 *
 * Stopping is three things - stop following the room, silence the audio, and
 * do neither if this is not the room you are actually in - and it is reachable
 * from the dock, the profile and the console. Three copies of that would
 * disagree within a release; the one that forgot to pause is the one that
 * leaves a broadcast playing under a page that says you left it.
 */

/**
 * Follow a broadcast.
 *
 * Tuning is deliberately *global*, not owned by the page you clicked from.
 * `RoomFollower` in the shell picks it up and keeps it fed, which is what lets
 * you carry on listening after you navigate away - the thing §9.1 exists to
 * protect, applied to a room instead of a track.
 */
export function startListening(room: Room): void {
  useRoomTuning.getState().tune(
    {
      id: room.id,
      username: room.username,
      name: room.name,
      host: room.host.display_name,
      // Only meaningful for a studio broadcast; `RoomAudio` keeps it current
      // from there on. See the note on `Tuned.streaming`.
      streaming:
        room.source === 'stream' && room.now_playing
          ? {
              title: room.now_playing.title,
              artist: room.now_playing.artist.display_name,
            }
          : null,
    },
    {
      position_ms: room.position_ms,
      server_time: room.server_time,
      paused: room.paused,
      track_id: room.now_playing?.id ?? null,
    },
  );
}

/** Whether this broadcast is the one currently being followed. */
export function isListeningTo(username: string): boolean {
  return useRoomTuning.getState().tuned?.username === username;
}

/**
 * Stop following, and go quiet.
 *
 * Unlike `leave()` on its own, which deliberately does *not* stop the audio:
 * somebody who skips to another track from the dock has left the room but is
 * still listening to something. Pressing "stop listening" means the opposite,
 * and leaving a broadcast playing after it would be the loudest possible bug.
 */
export function stopListening(): void {
  useRoomTuning.getState().leave();

  const player = usePlayer.getState();

  if (player.playing) player.toggle();
}
